'use client';
import Link from 'next/link';
import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { pages } from '@/constants';
import {
  Avatar,
  Badge,
  Container,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import userContained from '/public/assets/svg/user-outlined.svg';
import Image from 'next/image';
import logo from '/public/assets/svg/logo.svg';
import {
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation
} from '@/redux/api/notificationsApi';
import { usePathname } from 'next/navigation';
import { useGetUserQuery } from '@/redux/api/userApi';
import { useLogout } from '@/hooks/useLogout';
import { COLORS } from '@/constants/colors';
import { classes } from './styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatMessageTime } from '@/utils/helperFunctions';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';

interface Props {
  readonly window?: () => Window;
  isCritical?: boolean;
}

const UserProfileSkeleton = () => {
  return (
    <Box display="flex" ml={2} alignItems="center">
      <Box
        sx={{
          height: '48px',
          width: '48px',
          border: `1px solid ${COLORS.borderColor}`,
          borderRadius: 2,
          mr: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Skeleton variant="circular" width={24} height={24} />
      </Box>
      <Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }}>
        <Skeleton variant="circular" width={48} height={48} />
        <Stack ml={1.5}>
          <Box display="flex" alignItems="center">
            <Skeleton variant="text" width={120} height={24} />
          </Box>
          <Skeleton variant="text" width={180} height={16} />
        </Stack>
      </Box>
    </Box>
  );
};

export default function Navbar(props: Readonly<Props>) {
  const { window, isCritical = true } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openNotificationMenu, setOpenNotificationMenu] =
    React.useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down('sm'));
  const pathname = usePathname();
  const { handleLogout } = useLogout();

  const { data: notificationsData, isLoading: isLoadingNotifications } =
    useGetNotificationsQuery({
      read_status: 'any',
      isCritical
    });

  const [markNotificationAsRead, { isLoading: isMarkingAsRead }] =
    useMarkNotificationAsReadMutation();

  const unreadConversationsCount = useSelector(
    (state: RootState) => state?.chat?.unreadConversationsCount
  );

  const notificationsLoader = isLoadingNotifications && !notificationsData;

  const unreadCount = React.useMemo(() => {
    if (!notificationsData?.data) return 0;
    return notificationsData.data.filter((n: { isRead: boolean }) => !n.isRead)
      .length;
  }, [notificationsData]);

  const {
    data: userData,
    isLoading: isLoadingUser,
    isError: isUserError
  } = useGetUserQuery({
    isCritical
  });

  const handleDrawerToggle = React.useCallback(() => {
    setMobileOpen((prevState) => !prevState);
  }, []);

  const handleCloseUserMenu = () => {
    setOpenNotificationMenu(null);
  };

  const markAsRead = (notificationId: string) => {
    markNotificationAsRead(notificationId);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenNotificationMenu(event.currentTarget);
  };

  const drawer = React.useMemo(
    () => (
      <Box
        onClick={handleDrawerToggle}
        sx={{ textAlign: 'center', paddingY: 1 }}
        data-test-id="drawer"
      >
        <Image src={logo} height={25} width={135} alt="logo" />
        <Divider />
        <List>
          {[...pages, { title: 'Profile', link: '/profile' }].map((item) => (
            <ListItem key={item.title} disablePadding>
              <Link
                href={`${item.link.toLowerCase()}`}
                style={{
                  width: '100%',
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                <ListItemButton
                  sx={{ textAlign: 'center' }}
                  data-test-id={`drawer-item-${item}`}
                >
                  <ListItemText primary={item.title} />
                </ListItemButton>
              </Link>
            </ListItem>
          ))}
          {userData && !isUserError && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{ textAlign: 'center' }}
              >
                <ListItemText
                  primary="Logout"
                  sx={{ color: theme.palette.error.main }}
                />
              </ListItemButton>
            </ListItem>
          )}
          {(!userData || isUserError) && (
            <ListItem disablePadding>
              <Link
                href="/login"
                style={{
                  width: '100%',
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                <ListItemButton sx={{ textAlign: 'center' }}>
                  <ListItemText primary="Login" />
                </ListItemButton>
              </Link>
            </ListItem>
          )}
        </List>
      </Box>
    ),
    [
      handleDrawerToggle,
      handleLogout,
      isUserError,
      theme.palette.error.main,
      userData
    ]
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const renderUserProfile = () => {
    if (!userData) return null;

    return (
      <Box display="flex" ml={2} alignItems="center">
        <Link href="/chat" style={{ textDecoration: 'none' }} passHref>
          <Box
            sx={{
              ...classes.iconBox,
              mr: 1,
              backgroundColor:
                pathname === '/chat' ? COLORS.bgBlue : 'transparent'
            }}
          >
            <IconButton
              size="large"
              aria-label="show chat notifications"
              color="inherit"
              sx={{
                borderRadius: 2,
                '&:hover': {
                  borderRadius: 2
                }
              }}
            >
              <Badge badgeContent={unreadConversationsCount ?? 0} color="error">
                <Image
                  height={24}
                  width={24}
                  alt="message_Icon"
                  src="/assets/svg/message.svg"
                />
              </Badge>
            </IconButton>
          </Box>
        </Link>
        <Box sx={classes.iconBox}>
          <IconButton
            size="large"
            aria-label="show notifications"
            color="inherit"
            onClick={handleOpenUserMenu}
            sx={{
              borderRadius: 2,
              '&:hover': {
                borderRadius: 2
              }
            }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <Image
                height={24}
                width={24}
                alt="notification_Icon"
                src="/assets/svg/bell-outlined.svg"
              />
            </Badge>
          </IconButton>
          <Menu
            sx={{
              mt: '45px',
              maxWidth: '440px',
              minWidth: '320px',
              width: '100%'
            }}
            id="menu-appbar"
            anchorEl={openNotificationMenu}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            open={Boolean(openNotificationMenu)}
            onClose={handleCloseUserMenu}
            disableScrollLock={true}
          >
            <Box sx={{ width: '100%', py: 1.5 }}>
              <Typography textAlign="center" fontWeight={'500'}>
                Notifications
              </Typography>
            </Box>
            <Divider />
            <Box
              sx={{
                maxHeight: '350px',
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 0
              }}
            >
              {notificationsLoader ? (
                [...Array(10)].map((_, index) => (
                  <Box key={index} sx={{ p: 1, width: '100%' }}>
                    <Skeleton
                      variant="rectangular"
                      width="320px"
                      height={40}
                      sx={{ borderRadius: 1 }}
                    />
                  </Box>
                ))
              ) : (
                <>
                  {notificationsData?.data &&
                  notificationsData.data.length > 0 ? (
                    notificationsData.data.map(
                      (notification: {
                        isRead: boolean;
                        _id: string;
                        message: string;
                        createdAt: string;
                      }) => (
                        <MenuItem
                          key={notification?._id}
                          onClick={
                            isMarkingAsRead || notification?.isRead
                              ? undefined
                              : () => markAsRead(notification?._id)
                          }
                          sx={{
                            cursor: isMarkingAsRead ? 'not-allowed' : 'pointer',
                            p: 0.5
                          }}
                        >
                          <Stack
                            direction={'row'}
                            alignItems={'center'}
                            sx={{
                              width: '100%',
                              p: 0.5,
                              backgroundColor:
                                notification?.isRead === false
                                  ? COLORS.bgBlue
                                  : 'transparent',
                              position: 'relative'
                            }}
                          >
                            <Box
                              sx={{
                                height: '36px',
                                width: '36px',
                                borderRadius: 2,
                                backgroundColor: COLORS.greenPrimary,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                p: 1.5,
                                position: 'relative'
                              }}
                            >
                              <CheckCircleIcon
                                height={24}
                                width={24}
                                sx={{ color: COLORS.white }}
                              />
                              {!notification?.isRead && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: -2,
                                    right: -2,
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: '#FF0000',
                                    border: '1px solid white'
                                  }}
                                />
                              )}
                            </Box>
                            <Stack ml={1}>
                              <Typography
                                sx={{
                                  textOverflow: 'ellipsis',
                                  maxLines: 2,
                                  overflow: 'hidden',
                                  width: '100%',
                                  maxWidth: '320px'
                                }}
                                fontSize={14}
                              >
                                {notification?.message}
                              </Typography>
                              <Typography mt={1} fontSize={14}>
                                {formatMessageTime(notification?.createdAt)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </MenuItem>
                      )
                    )
                  ) : (
                    <Box sx={{ p: 2, minWidth: '250px', textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        No notifications
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Menu>
        </Box>
        <Link
          href="/profile?tab=profileSummary"
          style={{
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }}>
            <Avatar
              src={userData?.profile_image_url}
              sx={{
                width: 48,
                height: 48,
                backgroundColor: '#E8ECF4'
              }}
            >
              {!userData?.profile_image_url && (
                <Image
                  src={userContained}
                  alt="avatar icon"
                  width={32}
                  height={32}
                  style={{ padding: 0, margin: 0, display: 'block' }}
                />
              )}
            </Avatar>
            <Stack ml={1.5}>
              <Box display="flex" alignItems="center">
                <Typography color="primary" variant="body1">
                  {`${userData.first_name} ${userData.last_name}`}
                </Typography>
              </Box>
              <Typography fontSize={10} variant="body2">
                {userData.email}
              </Typography>
            </Stack>
          </Box>
        </Link>
      </Box>
    );
  };

  const renderAuthSection = () => {
    if (isLoadingUser) {
      return <UserProfileSkeleton />;
    } else if (userData && !isUserError) {
      return renderUserProfile();
    } else {
      return (
        <Link href="/login" style={{ textDecoration: 'none' }} passHref>
          <Button
            variant="outlined"
            sx={{
              ...classes.getStartedBtn,
              display: { xs: 'none', md: 'block' }
            }}
            data-test-id="get-started-btn-desktop"
          >
            Login
          </Button>
        </Link>
      );
    }
  };

  return (
    <>
      <AppBar component="nav" sx={classes.root} data-test-id="app-bar">
        <Container disableGutters>
          <Toolbar sx={classes.justifyBetween}>
            <Link
              href="/"
              style={{ textDecoration: 'none', cursor: 'pointer' }}
              passHref
            >
              <Image
                src={logo}
                height={isXsOrSm ? 25 : 44}
                width={isXsOrSm ? 135 : 235}
                alt="logo"
              />
            </Link>

            <Box display="flex" component="nav">
              <Box
                sx={{ display: { xs: 'none', md: 'block' } }}
                data-test-id="desktop-nav"
              >
                <Box display="flex" alignItems="center">
                  {pages.map((item) => {
                    const isActive = pathname === item.link;
                    return (
                      <Link
                        href={`${item.link.toLowerCase()}`}
                        key={item.title}
                        passHref
                      >
                        <Button
                          sx={{
                            ...classes.navItem,
                            color: isActive
                              ? theme.palette.primary.main
                              : theme.palette.text.primary
                          }}
                          data-test-id={`nav-item-${item}`}
                        >
                          {item.title}
                        </Button>
                      </Link>
                    );
                  })}

                  {renderAuthSection()}
                </Box>
              </Box>

              {/* Only show login button for mobile if not loading and not logged in */}
              {!isLoadingUser && (!userData || isUserError) && (
                <Link href="/login" style={{ textDecoration: 'none' }} passHref>
                  <Button
                    variant="outlined"
                    sx={{
                      ...classes.getStartedBtn,
                      display: { xs: 'block', md: 'none' } // Show on mobile, hide on desktop
                    }}
                    data-test-id="get-started-btn-mobile"
                  >
                    Login
                  </Button>
                </Link>
              )}

              <IconButton
                color="primary"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ ml: 2, display: { md: 'none' } }}
                data-test-id="menu-icon"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={classes.drawer}
          data-test-id="mobile-drawer"
        >
          {drawer}
        </Drawer>
      </nav>
    </>
  );
}
